import { useMemo } from 'react';
import { addDagen, fmtDag, maandagVan, verschilDagen } from '@ep/core';
import type { Data, ProjectRij, TaakRij } from '../lib/data';

interface Rij { soort: 'groep' | 'taak' | 'pakket'; label: string; van?: string | null; tot?: string | null; deadline?: string | null; klaar?: boolean; open?: boolean; titel?: string }

/** Tijdlijn van één project, gegroepeerd per werkpakket (zoals de verbeterde Power App-tijdlijn). */
export function ProjectGantt({ d, project, labelBreedte = 280 }: { d: Data; project: ProjectRij; labelBreedte?: number }) {
  const { rijen, start, dagen } = useMemo(() => {
    const taken = d.taken.filter((t) => t.projectId === project.id);
    const wps = d.werkpakketten.filter((w) => w.projectId === project.id).sort((a, b) => a.volgorde - b.volgorde);
    const tps = d.takenpakketten.filter((t) => t.projectId === project.id);
    const r: Rij[] = [];
    const eng = (id?: string | null) => d.engineers.find((e) => e.id === id)?.naam ?? 'nog niet gepland';
    const taakRij = (t: TaakRij): Rij => ({
      soort: 'taak',
      label: `${t.titel} · ${eng(t.engineerId)}`,
      van: t.startdatum,
      tot: t.einddatum ?? t.startdatum,
      klaar: t.terugmelding === 'KLAAR',
      open: t.status !== 'GEPLAND',
      titel: `${t.titel}\n${eng(t.engineerId)}\n${fmtDag(t.startdatum)}${t.einddatum && t.einddatum !== t.startdatum ? ' – ' + fmtDag(t.einddatum) : ''}`,
    });
    for (const w of wps) {
      r.push({ soort: 'groep', label: w.naam, deadline: w.deadline });
      for (const tp of tps.filter((x) => x.werkpakketId === w.id).sort((a, b) => a.volgorde - b.volgorde)) {
        const tk = taken.filter((t) => t.takenpakketId === tp.id);
        if (!tk.length) r.push({ soort: 'pakket', label: `${tp.naam} (${tp.uren}u, nog niet ingepland)` });
        for (const t of tk) r.push(taakRij(t));
      }
      for (const t of taken.filter((t) => t.werkpakketId === w.id && !t.takenpakketId)) r.push(taakRij(t));
    }
    const los = taken.filter((t) => !t.werkpakketId && !(t.takenpakketId && tps.some((x) => x.id === t.takenpakketId && x.werkpakketId)));
    if (los.length) {
      r.push({ soort: 'groep', label: 'Direct onder het project' });
      for (const t of los) r.push(taakRij(t));
    }
    const datums = [project.start, project.livegang, project.oplevering, ...taken.flatMap((t) => [t.startdatum, t.einddatum]), ...wps.map((w) => w.deadline), d.vandaag].filter(Boolean) as string[];
    const min = maandagVan(datums.sort()[0]);
    const max = addDagen(datums.sort().slice(-1)[0], 7);
    return { rijen: r, start: min, dagen: Math.max(28, verschilDagen(min, max) + 1) };
  }, [d, project]);

  const pct = (x: string) => (verschilDagen(start, x) / dagen) * 100;
  const breedte = (a: string, b: string) => ((verschilDagen(a, b) + 1) / dagen) * 100;
  const weken: string[] = [];
  for (let w = start; verschilDagen(start, w) < dagen; w = addDagen(w, 7)) weken.push(w);
  const elkeN = Math.ceil(weken.length / 26);

  return (
    <div className="gantt">
      <div className="gantt-rij" style={{ borderBottom: '1px solid var(--lijn)' }}>
        <div className="gantt-label" style={{ width: labelBreedte }} />
        <div className="gantt-baan gantt-schaal">
          {weken.map((w, i) =>
            i % elkeN === 0 ? (
              <div key={w} className="tik" style={{ left: `${pct(w)}%` }}>
                {fmtDag(w, 'datum')}
              </div>
            ) : null,
          )}
        </div>
      </div>
      {rijen.map((r, i) => (
        <div className="gantt-rij" key={i}>
          <div className={`gantt-label ${r.soort === 'groep' ? 'groep' : ''}`} style={{ width: labelBreedte, paddingLeft: r.soort === 'groep' ? 8 : 22 }} title={r.label}>
            {r.label}
            {r.deadline ? <span className="mute klein"> · deadline {fmtDag(r.deadline, 'datum')}</span> : null}
          </div>
          <div className="gantt-baan">
            <div className="gantt-lijn" style={{ left: `${pct(d.vandaag)}%` }} title="Vandaag" />
            {project.livegang && <div className="gantt-lijn live" style={{ left: `${pct(project.livegang)}%` }} title={`Livegang ${fmtDag(project.livegang, 'jaar')}`} />}
            {r.van && r.tot && <div className={`gantt-staaf ${r.klaar ? 'klaar' : ''} ${r.open ? 'open' : ''}`} style={{ left: `${pct(r.van)}%`, width: `${breedte(r.van, r.tot)}%` }} title={r.titel} />}
            {r.deadline && <div className="gantt-ruit deadline" style={{ left: `calc(${pct(r.deadline)}% - 6px)` }} title={`Deadline ${fmtDag(r.deadline, 'jaar')}`} />}
          </div>
        </div>
      ))}
      {!rijen.length && <div className="leeg">Nog geen werkpakketten of taken.</div>}
    </div>
  );
}
